'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { authApi } from '@/lib/api/auth';
import type { BotProtection, ChallengeResponse } from '@/lib/api/types';

interface UseBotProtectionReturn {
  isReady: boolean;
  honeypotProps: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
  getBotProtection: () => BotProtection;
  refresh: () => void;
}

export function useBotProtection(): UseBotProtectionReturn {
  const [isReady, setIsReady] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const challengeRef = useRef<ChallengeResponse | null>(null);
  const nonceRef = useRef<string>('');
  const workerRef = useRef<Worker | null>(null);

  const startSolving = useCallback(() => {
    nonceRef.current = '';
    challengeRef.current = null;

    authApi.getChallenge().then((challenge) => {
      challengeRef.current = challenge;

      const worker = new Worker('/pow-worker.js');
      workerRef.current = worker;

      worker.onmessage = (e: MessageEvent<{ nonce: string; found: boolean }>) => {
        if (e.data.found) {
          nonceRef.current = e.data.nonce;
          setIsReady(true);
          worker.terminate();
          workerRef.current = null;
        }
      };

      worker.postMessage({
        challengeData: challenge.challenge_data,
        difficulty: challenge.difficulty,
      });
    }).catch(() => {
      // Graceful fallback: form isn't permanently stuck
      setIsReady(true);
    });
  }, []);

  const refresh = useCallback(() => {
    setIsReady(false);
    startSolving();
  }, [startSolving]);

  useEffect(() => {
    startSolving();

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [startSolving]);

  const getBotProtection = useCallback((): BotProtection => {
    return {
      hp_website: honeypot,
      ts: challengeRef.current?.ts ?? '',
      pow_challenge: challengeRef.current?.challenge_id ?? '',
      pow_nonce: nonceRef.current,
    };
  }, [honeypot]);

  const honeypotProps = {
    value: honeypot,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setHoneypot(e.target.value),
  };

  return { isReady, honeypotProps, getBotProtection, refresh };
}
